/**
 * A conservative SQL formatter for the query editor.
 *
 * The rule this is built around: formatting may change WHITESPACE and the CASE
 * OF KEYWORDS, and nothing else. Not a token, not a literal, not a comment, not
 * the order of anything. A formatter that occasionally rewrites your query is
 * worse than no formatter, because you will not notice until it runs.
 *
 * That is why this tokenises rather than pattern-matching on the raw text:
 * `WHERE note = 'select * from x'` and `-- order by id` contain keywords that
 * must be left exactly as they are. It is also why the test suite's first
 * assertion is that the token stream survives formatting unchanged.
 *
 * Deliberately hand-rolled rather than pulling in a formatter library: this is
 * a couple of hundred lines with an invariant that can be tested, against a
 * dependency that would need auditing for the same guarantee. If the output
 * ever needs to be cleverer than this, swapping in a library is the moment to
 * reconsider — not before.
 */

// Clauses that start a new line at their nesting level.
const TOP_LEVEL = new Set([
  'SELECT', 'FROM', 'WHERE', 'HAVING', 'WINDOW', 'VALUES', 'SET', 'RETURNING',
  'UNION', 'INTERSECT', 'EXCEPT', 'LIMIT', 'OFFSET', 'FETCH',
  'INSERT', 'UPDATE', 'DELETE', 'WITH',
]);

// Two-word clauses, matched as a pair so "GROUP" alone is not a clause.
const TOP_LEVEL_PAIRS = new Set(['GROUP BY', 'ORDER BY', 'PARTITION BY', 'INSERT INTO', 'DELETE FROM']);

// Join keywords start a line; the ON that follows is indented under it.
const JOIN_STARTERS = new Set(['JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'NATURAL', 'STRAIGHT_JOIN']);

// The rest of a join phrase, which must stay on the line its starter opened —
// "LEFT OUTER JOIN" is one thing, and breaking after every word of it that
// happens to be a keyword turns it into three lines.
const JOIN_CONTINUATIONS = new Set([...JOIN_STARTERS, 'OUTER']);

// Keywords that get their own line, indented one level (the boolean operators
// in a WHERE, and the ON of a join).
const INDENTED = new Set(['AND', 'OR', 'ON']);

const KEYWORDS = new Set([
  ...TOP_LEVEL, ...JOIN_STARTERS, ...INDENTED,
  'GROUP', 'ORDER', 'PARTITION', 'BY', 'INTO', 'AS', 'ON', 'USING', 'DISTINCT', 'ALL',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'IN', 'IS', 'NULL', 'NOT', 'LIKE', 'ILIKE',
  'BETWEEN', 'EXISTS', 'ASC', 'DESC', 'INNER', 'OUTER', 'CREATE', 'ALTER', 'DROP',
  'TABLE', 'VIEW', 'INDEX', 'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'UNIQUE',
  'DEFAULT', 'CONSTRAINT', 'CASCADE', 'TRUE', 'FALSE', 'BEGIN', 'COMMIT', 'ROLLBACK',
  'EXPLAIN', 'ANALYZE', 'REPLACE', 'IF', 'CAST', 'OVER', 'FILTER', 'RECURSIVE',
]);

/**
 * SQL as a list of tokens.
 *
 * Strings, quoted identifiers and comments come back as single opaque tokens so
 * nothing downstream can look inside them — that is the whole safety property.
 */
export function tokenizeSql(sql) {
  const text = String(sql ?? '');
  const tokens = [];
  let i = 0;

  const push = (type, value) => tokens.push({ type, value });

  while (i < text.length) {
    const c = text[i];

    if (/\s/.test(c)) {
      let j = i;
      while (j < text.length && /\s/.test(text[j])) j += 1;
      // Blank lines are the one whitespace fact worth keeping: they are how
      // people separate statements in a script.
      push('space', text.slice(i, j));
      i = j;
      continue;
    }

    if (c === '-' && text[i + 1] === '-') {
      const end = text.indexOf('\n', i);
      const stop = end === -1 ? text.length : end;
      push('comment-line', text.slice(i, stop));
      i = stop;
      continue;
    }

    if (c === '/' && text[i + 1] === '*') {
      const end = text.indexOf('*/', i + 2);
      const stop = end === -1 ? text.length : end + 2;
      push('comment-block', text.slice(i, stop));
      i = stop;
      continue;
    }

    // Quoted things. Doubling the quote is the escape in every engine here, and
    // MySQL also honours a backslash inside single quotes.
    if (c === "'" || c === '"' || c === '`') {
      let j = i + 1;
      while (j < text.length) {
        if (c === "'" && text[j] === '\\') {
          j += 2;
          continue;
        }
        if (text[j] === c) {
          if (text[j + 1] === c) {
            j += 2;
            continue;
          }
          j += 1;
          break;
        }
        j += 1;
      }
      push(c === "'" ? 'string' : 'quoted-ident', text.slice(i, j));
      i = j;
      continue;
    }

    if (c === '(' || c === ')' || c === ',' || c === ';') {
      push(c === ',' ? 'comma' : c === ';' ? 'semicolon' : c === '(' ? 'open' : 'close', c);
      i += 1;
      continue;
    }

    if (/[A-Za-z_$@#]/.test(c)) {
      let j = i;
      while (j < text.length && /[A-Za-z0-9_$@#.]/.test(text[j])) j += 1;
      const word = text.slice(i, j);
      push(KEYWORDS.has(word.toUpperCase()) ? 'keyword' : 'word', word);
      i = j;
      continue;
    }

    if (/[0-9]/.test(c)) {
      let j = i;
      while (j < text.length && /[0-9.eE+-]/.test(text[j])) {
        // Only consume a sign when it is part of an exponent.
        if ((text[j] === '+' || text[j] === '-') && !/[eE]/.test(text[j - 1])) break;
        j += 1;
      }
      push('number', text.slice(i, j));
      i = j;
      continue;
    }

    // Operators and anything else: one character at a time, but glue the
    // multi-character comparisons together so `<=` does not become `< =`.
    const two = text.slice(i, i + 2);
    if (['<=', '>=', '<>', '!=', '||', '::', '->', '=>'].includes(two)) {
      push('operator', two);
      i += 2;
      continue;
    }
    push('operator', c);
    i += 1;
  }

  return tokens;
}

/** Everything that is not whitespace — the stream formatting must preserve. */
export function significantTokens(sql) {
  return tokenizeSql(sql).filter((t) => t.type !== 'space');
}

function upper(token, uppercaseKeywords) {
  return token.type === 'keyword' && uppercaseKeywords ? token.value.toUpperCase() : token.value;
}

/**
 * Reformat `sql`.
 *
 * `indent` is the string used per nesting level; `uppercaseKeywords` may be
 * turned off by anyone who writes lowercase SQL on purpose.
 */
export function formatSql(sql, { indent = '  ', uppercaseKeywords = true } = {}) {
  const tokens = tokenizeSql(sql).filter((t) => t.type !== 'space');
  if (tokens.length === 0) return '';

  const out = [];
  let depth = 0;
  let line = '';
  // True while emitting the words of a single join phrase.
  let inJoinPhrase = false;

  const flush = () => {
    if (line.trim()) out.push(line.trimEnd());
    line = '';
  };
  const startLine = (extra = 0) => {
    flush();
    line = indent.repeat(Math.max(0, depth + extra));
  };
  const append = (text, { space = true } = {}) => {
    if (line.trim() === '') line += text;
    else line += (space ? ' ' : '') + text;
  };

  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];
    const next = tokens[i + 1];
    const word = t.type === 'keyword' ? t.value.toUpperCase() : null;
    const pair = word && next?.type === 'keyword' ? `${word} ${next.value.toUpperCase()}` : null;

    if (t.type === 'comment-line' || t.type === 'comment-block') {
      startLine();
      append(t.value);
      if (t.type === 'comment-line') flush();
      continue;
    }

    if (t.type === 'semicolon') {
      append(';', { space: false });
      flush();
      // A blank line between statements, the way scripts are actually written.
      if (next) out.push('');
      depth = 0;
      continue;
    }

    if (t.type === 'open') {
      append('(', { space: line.trim() !== '' && !/[([,]$/.test(line.trim()) && !endsWithFunctionName(line) });
      depth += 1;
      continue;
    }

    if (t.type === 'close') {
      depth = Math.max(0, depth - 1);
      append(')', { space: false });
      continue;
    }

    if (t.type === 'comma') {
      append(',', { space: false });
      continue;
    }

    if (pair && TOP_LEVEL_PAIRS.has(pair)) {
      startLine();
      append(`${upper(t, uppercaseKeywords)} ${upper(next, uppercaseKeywords)}`);
      i += 1;
      continue;
    }

    if (word && TOP_LEVEL.has(word)) {
      inJoinPhrase = false;
      startLine();
      append(upper(t, uppercaseKeywords));
      continue;
    }

    if (word && JOIN_STARTERS.has(word)) {
      if (!inJoinPhrase) startLine();
      append(upper(t, uppercaseKeywords));
      inJoinPhrase = true;
      continue;
    }
    if (inJoinPhrase && word && JOIN_CONTINUATIONS.has(word)) {
      append(upper(t, uppercaseKeywords));
      continue;
    }

    if (word && INDENTED.has(word)) {
      inJoinPhrase = false;
      startLine(1);
      append(upper(t, uppercaseKeywords));
      continue;
    }

    inJoinPhrase = false;
    append(upper(t, uppercaseKeywords), { space: !isTightAfter(line) });
  }

  flush();
  return out.join('\n');
}

/** `count(` should not become `count (`. */
function endsWithFunctionName(line) {
  return /[A-Za-z0-9_$#]$/.test(line);
}

/** No space after an opening paren or `::`. */
function isTightAfter(line) {
  return /\($/.test(line) || /::$/.test(line);
}
